// src/authorization/actions.ts
var AUTHORIZATION_ACTIONS = {
  // CRUD operations
  CREATE: "create",
  READ: "read",
  UPDATE: "update",
  DELETE: "delete",
  // Full access
  MANAGE: "manage",
  // Specialized actions
  ASSIGN: "assign",
  // Assign workers to appointments
  APPROVE: "approve",
  // Approve time off, changes
  EXPORT: "export",
  // Export data/reports
  VIEW_ANALYTICS: "view_analytics",
  VIEW_SCHEDULE: "view_schedule"
};
function isAuthorizationAction(action) {
  return Object.values(AUTHORIZATION_ACTIONS).includes(action);
}

// src/authorization/subjects.ts
var AUTHORIZATION_SUBJECTS = {
  // Core entities
  APPOINTMENT: "Appointment",
  MY_APPOINTMENT: "MyAppointment",
  WORKER: "Worker",
  WORKER_SALON: "WorkerSalon",
  CUSTOMER: "Customer",
  SERVICE: "Service",
  SERVICE_CATEGORY: "ServiceCategory",
  SERVICE_TEMPLATE: "ServiceTemplate",
  DISCOUNT: "Discount",
  // Salon management
  SALON: "Salon",
  SALON_SETTINGS: "SalonSettings",
  SALON_CONFIGURATION: "SalonConfiguration",
  PREMIUM_SALON_CONFIGURATION: "PremiumSalonConfiguration",
  SALON_REVIEW: "SalonReview",
  PROMO_EVENT: "PromoEvent",
  GOOGLE_BUSINESS_CONNECTION: "GoogleBusinessConnection",
  GOOGLE_BUSINESS_MANAGER_ACCOUNT: "GoogleBusinessManagerAccount",
  // Time management
  HOLIDAY: "Holiday",
  AVAILABILITY: "Availability",
  SCHEDULE: "Schedule",
  // Analytics & Reporting
  REPORT: "Report",
  ANALYTICS: "Analytics",
  AUDIT_LOG: "AuditLog",
  // User management
  USER_ROLE: "UserRole",
  MEMBER_PASSWORD: "MemberPassword",
  // Partner management
  PARTNER_GROUP: "PartnerGroup",
  PARTNER_MEMBER: "PartnerMember",
  PARTNER_FRANCHISE_ASSIGNMENT: "PartnerFranchiseAssignment",
  FRANCHISE: "Franchise",
  // System
  ALL: "all"
};
function isAuthorizationSubject(subject) {
  return Object.values(AUTHORIZATION_SUBJECTS).includes(subject);
}

// src/authorization/abilities.ts
function defineOwnerAbilities(can, cannot) {
  can(AUTHORIZATION_ACTIONS.MANAGE, AUTHORIZATION_SUBJECTS.ALL);
  can(AUTHORIZATION_ACTIONS.READ, AUTHORIZATION_SUBJECTS.PREMIUM_SALON_CONFIGURATION);
  cannot(AUTHORIZATION_ACTIONS.DELETE, AUTHORIZATION_SUBJECTS.WORKER);
}
function defineManagerAbilities(can, cannot) {
  can(AUTHORIZATION_ACTIONS.READ, AUTHORIZATION_SUBJECTS.SALON_CONFIGURATION);
  can(AUTHORIZATION_ACTIONS.CREATE, AUTHORIZATION_SUBJECTS.MY_APPOINTMENT);
  can(AUTHORIZATION_ACTIONS.READ, AUTHORIZATION_SUBJECTS.MY_APPOINTMENT);
  can(AUTHORIZATION_ACTIONS.UPDATE, AUTHORIZATION_SUBJECTS.MY_APPOINTMENT);
  can(AUTHORIZATION_ACTIONS.DELETE, AUTHORIZATION_SUBJECTS.MY_APPOINTMENT);
  can(AUTHORIZATION_ACTIONS.ASSIGN, AUTHORIZATION_SUBJECTS.MY_APPOINTMENT);
  can(AUTHORIZATION_ACTIONS.CREATE, AUTHORIZATION_SUBJECTS.APPOINTMENT);
  can(AUTHORIZATION_ACTIONS.READ, AUTHORIZATION_SUBJECTS.APPOINTMENT);
  can(AUTHORIZATION_ACTIONS.UPDATE, AUTHORIZATION_SUBJECTS.APPOINTMENT);
  can(AUTHORIZATION_ACTIONS.DELETE, AUTHORIZATION_SUBJECTS.APPOINTMENT);
  can(AUTHORIZATION_ACTIONS.ASSIGN, AUTHORIZATION_SUBJECTS.APPOINTMENT);
  can(AUTHORIZATION_ACTIONS.CREATE, AUTHORIZATION_SUBJECTS.WORKER);
  can(AUTHORIZATION_ACTIONS.READ, AUTHORIZATION_SUBJECTS.WORKER);
  can(AUTHORIZATION_ACTIONS.UPDATE, AUTHORIZATION_SUBJECTS.WORKER);
  can(AUTHORIZATION_ACTIONS.VIEW_SCHEDULE, AUTHORIZATION_SUBJECTS.SCHEDULE);
  cannot(AUTHORIZATION_ACTIONS.DELETE, AUTHORIZATION_SUBJECTS.WORKER);
  can(AUTHORIZATION_ACTIONS.CREATE, AUTHORIZATION_SUBJECTS.CUSTOMER);
  can(AUTHORIZATION_ACTIONS.READ, AUTHORIZATION_SUBJECTS.CUSTOMER);
  can(AUTHORIZATION_ACTIONS.UPDATE, AUTHORIZATION_SUBJECTS.CUSTOMER);
  can(AUTHORIZATION_ACTIONS.DELETE, AUTHORIZATION_SUBJECTS.CUSTOMER);
  can(AUTHORIZATION_ACTIONS.VIEW_ANALYTICS, AUTHORIZATION_SUBJECTS.CUSTOMER);
  cannot(AUTHORIZATION_ACTIONS.EXPORT, AUTHORIZATION_SUBJECTS.CUSTOMER);
  can(AUTHORIZATION_ACTIONS.CREATE, AUTHORIZATION_SUBJECTS.HOLIDAY);
  can(AUTHORIZATION_ACTIONS.READ, AUTHORIZATION_SUBJECTS.HOLIDAY);
  can(AUTHORIZATION_ACTIONS.UPDATE, AUTHORIZATION_SUBJECTS.HOLIDAY);
  cannot(AUTHORIZATION_ACTIONS.DELETE, AUTHORIZATION_SUBJECTS.HOLIDAY);
  can(AUTHORIZATION_ACTIONS.READ, AUTHORIZATION_SUBJECTS.SALON_SETTINGS);
  can(AUTHORIZATION_ACTIONS.UPDATE, AUTHORIZATION_SUBJECTS.SALON_SETTINGS);
  can(AUTHORIZATION_ACTIONS.READ, AUTHORIZATION_SUBJECTS.USER_ROLE);
  can(AUTHORIZATION_ACTIONS.MANAGE, AUTHORIZATION_SUBJECTS.SERVICE);
  can(AUTHORIZATION_ACTIONS.VIEW_ANALYTICS, AUTHORIZATION_SUBJECTS.ANALYTICS);
  can(AUTHORIZATION_ACTIONS.CREATE, AUTHORIZATION_SUBJECTS.DISCOUNT);
  can(AUTHORIZATION_ACTIONS.READ, AUTHORIZATION_SUBJECTS.DISCOUNT);
  can(AUTHORIZATION_ACTIONS.UPDATE, AUTHORIZATION_SUBJECTS.DISCOUNT);
  can(AUTHORIZATION_ACTIONS.DELETE, AUTHORIZATION_SUBJECTS.DISCOUNT);
  can(AUTHORIZATION_ACTIONS.MANAGE, AUTHORIZATION_SUBJECTS.GOOGLE_BUSINESS_CONNECTION);
  cannot(AUTHORIZATION_ACTIONS.MANAGE, AUTHORIZATION_SUBJECTS.USER_ROLE);
  cannot(AUTHORIZATION_ACTIONS.EXPORT, AUTHORIZATION_SUBJECTS.REPORT);
}
function defineWorkerAbilities(can, cannot) {
  can(AUTHORIZATION_ACTIONS.READ, AUTHORIZATION_SUBJECTS.SALON_CONFIGURATION);
  can(AUTHORIZATION_ACTIONS.READ, AUTHORIZATION_SUBJECTS.APPOINTMENT);
  can(AUTHORIZATION_ACTIONS.READ, AUTHORIZATION_SUBJECTS.MY_APPOINTMENT);
  can(AUTHORIZATION_ACTIONS.UPDATE, AUTHORIZATION_SUBJECTS.MY_APPOINTMENT);
  cannot(AUTHORIZATION_ACTIONS.MANAGE, AUTHORIZATION_SUBJECTS.APPOINTMENT);
  can(AUTHORIZATION_ACTIONS.READ, AUTHORIZATION_SUBJECTS.WORKER);
  can(AUTHORIZATION_ACTIONS.UPDATE, AUTHORIZATION_SUBJECTS.WORKER);
  can(AUTHORIZATION_ACTIONS.VIEW_SCHEDULE, AUTHORIZATION_SUBJECTS.SCHEDULE);
  can(AUTHORIZATION_ACTIONS.READ, AUTHORIZATION_SUBJECTS.CUSTOMER);
  can(AUTHORIZATION_ACTIONS.READ, AUTHORIZATION_SUBJECTS.HOLIDAY);
  can(AUTHORIZATION_ACTIONS.READ, AUTHORIZATION_SUBJECTS.SERVICE);
  can(AUTHORIZATION_ACTIONS.READ, AUTHORIZATION_SUBJECTS.SERVICE_CATEGORY);
  can(AUTHORIZATION_ACTIONS.READ, AUTHORIZATION_SUBJECTS.DISCOUNT);
  can(AUTHORIZATION_ACTIONS.READ, AUTHORIZATION_SUBJECTS.SALON_SETTINGS);
}
function defineFranchiseAdminAbilities(can, cannot) {
  defineOwnerAbilities(can, cannot);
  can(AUTHORIZATION_ACTIONS.MANAGE, AUTHORIZATION_SUBJECTS.PREMIUM_SALON_CONFIGURATION);
}
function definePartnerGroupAdminAbilities(can, cannot) {
  can(AUTHORIZATION_ACTIONS.MANAGE, AUTHORIZATION_SUBJECTS.PARTNER_GROUP);
  can(AUTHORIZATION_ACTIONS.MANAGE, AUTHORIZATION_SUBJECTS.PARTNER_MEMBER);
  can(AUTHORIZATION_ACTIONS.MANAGE, AUTHORIZATION_SUBJECTS.PARTNER_FRANCHISE_ASSIGNMENT);
  can(AUTHORIZATION_ACTIONS.READ, AUTHORIZATION_SUBJECTS.FRANCHISE);
  can(AUTHORIZATION_ACTIONS.UPDATE, AUTHORIZATION_SUBJECTS.FRANCHISE);
  can(AUTHORIZATION_ACTIONS.READ, AUTHORIZATION_SUBJECTS.SALON);
  can(AUTHORIZATION_ACTIONS.UPDATE, AUTHORIZATION_SUBJECTS.SALON);
  can(AUTHORIZATION_ACTIONS.CREATE, AUTHORIZATION_SUBJECTS.SALON);
  cannot(AUTHORIZATION_ACTIONS.DELETE, AUTHORIZATION_SUBJECTS.SALON);
  can(AUTHORIZATION_ACTIONS.MANAGE, AUTHORIZATION_SUBJECTS.APPOINTMENT);
  can(AUTHORIZATION_ACTIONS.MANAGE, AUTHORIZATION_SUBJECTS.CUSTOMER);
  can(AUTHORIZATION_ACTIONS.MANAGE, AUTHORIZATION_SUBJECTS.SERVICE);
  can(AUTHORIZATION_ACTIONS.MANAGE, AUTHORIZATION_SUBJECTS.SERVICE_CATEGORY);
  can(AUTHORIZATION_ACTIONS.CREATE, AUTHORIZATION_SUBJECTS.WORKER);
  can(AUTHORIZATION_ACTIONS.READ, AUTHORIZATION_SUBJECTS.WORKER);
  can(AUTHORIZATION_ACTIONS.UPDATE, AUTHORIZATION_SUBJECTS.WORKER);
  cannot(AUTHORIZATION_ACTIONS.DELETE, AUTHORIZATION_SUBJECTS.WORKER);
  can(AUTHORIZATION_ACTIONS.MANAGE, AUTHORIZATION_SUBJECTS.SCHEDULE);
  can(AUTHORIZATION_ACTIONS.MANAGE, AUTHORIZATION_SUBJECTS.HOLIDAY);
  can(AUTHORIZATION_ACTIONS.MANAGE, AUTHORIZATION_SUBJECTS.AVAILABILITY);
  can(AUTHORIZATION_ACTIONS.VIEW_ANALYTICS, AUTHORIZATION_SUBJECTS.ANALYTICS);
  can(AUTHORIZATION_ACTIONS.EXPORT, AUTHORIZATION_SUBJECTS.REPORT);
  can(AUTHORIZATION_ACTIONS.READ, AUTHORIZATION_SUBJECTS.MEMBER_PASSWORD);
  can(AUTHORIZATION_ACTIONS.READ, AUTHORIZATION_SUBJECTS.SALON_CONFIGURATION);
  can(AUTHORIZATION_ACTIONS.UPDATE, AUTHORIZATION_SUBJECTS.SALON_CONFIGURATION);
  can(AUTHORIZATION_ACTIONS.READ, AUTHORIZATION_SUBJECTS.PREMIUM_SALON_CONFIGURATION);
  can(AUTHORIZATION_ACTIONS.UPDATE, AUTHORIZATION_SUBJECTS.PREMIUM_SALON_CONFIGURATION);
}
function definePartnerGroupMemberAbilities(can, cannot) {
  can(AUTHORIZATION_ACTIONS.READ, AUTHORIZATION_SUBJECTS.PARTNER_GROUP);
  can(AUTHORIZATION_ACTIONS.READ, AUTHORIZATION_SUBJECTS.PARTNER_MEMBER);
  can(AUTHORIZATION_ACTIONS.UPDATE, AUTHORIZATION_SUBJECTS.PARTNER_MEMBER);
  can(AUTHORIZATION_ACTIONS.READ, AUTHORIZATION_SUBJECTS.FRANCHISE);
  can(AUTHORIZATION_ACTIONS.READ, AUTHORIZATION_SUBJECTS.SALON);
  can(AUTHORIZATION_ACTIONS.UPDATE, AUTHORIZATION_SUBJECTS.SALON);
  can(AUTHORIZATION_ACTIONS.MANAGE, AUTHORIZATION_SUBJECTS.APPOINTMENT);
  can(AUTHORIZATION_ACTIONS.MANAGE, AUTHORIZATION_SUBJECTS.CUSTOMER);
  can(AUTHORIZATION_ACTIONS.READ, AUTHORIZATION_SUBJECTS.SERVICE);
  can(AUTHORIZATION_ACTIONS.READ, AUTHORIZATION_SUBJECTS.SERVICE_CATEGORY);
  can(AUTHORIZATION_ACTIONS.READ, AUTHORIZATION_SUBJECTS.WORKER);
  can(AUTHORIZATION_ACTIONS.UPDATE, AUTHORIZATION_SUBJECTS.WORKER);
  cannot(AUTHORIZATION_ACTIONS.CREATE, AUTHORIZATION_SUBJECTS.WORKER);
  cannot(AUTHORIZATION_ACTIONS.DELETE, AUTHORIZATION_SUBJECTS.WORKER);
  can(AUTHORIZATION_ACTIONS.READ, AUTHORIZATION_SUBJECTS.SCHEDULE);
  can(AUTHORIZATION_ACTIONS.VIEW_SCHEDULE, AUTHORIZATION_SUBJECTS.SCHEDULE);
  can(AUTHORIZATION_ACTIONS.READ, AUTHORIZATION_SUBJECTS.HOLIDAY);
  can(AUTHORIZATION_ACTIONS.VIEW_ANALYTICS, AUTHORIZATION_SUBJECTS.ANALYTICS);
  cannot(AUTHORIZATION_ACTIONS.EXPORT, AUTHORIZATION_SUBJECTS.REPORT);
  can(AUTHORIZATION_ACTIONS.READ, AUTHORIZATION_SUBJECTS.MEMBER_PASSWORD);
  can(AUTHORIZATION_ACTIONS.READ, AUTHORIZATION_SUBJECTS.SALON_CONFIGURATION);
  can(AUTHORIZATION_ACTIONS.READ, AUTHORIZATION_SUBJECTS.PREMIUM_SALON_CONFIGURATION);
  can(AUTHORIZATION_ACTIONS.UPDATE, AUTHORIZATION_SUBJECTS.PREMIUM_SALON_CONFIGURATION);
}
function defineSuperAdminAbilities(can) {
  can(AUTHORIZATION_ACTIONS.MANAGE, AUTHORIZATION_SUBJECTS.ALL);
}
var ROLE_ABILITIES = {
  owner: defineOwnerAbilities,
  manager: defineManagerAbilities,
  worker: defineWorkerAbilities,
  franchiseAdmin: defineFranchiseAdminAbilities,
  partnerGroupAdmin: definePartnerGroupAdminAbilities,
  partnerGroupMember: definePartnerGroupMemberAbilities,
  superAdmin: defineSuperAdminAbilities
};

// src/authorization/ability-factory.ts
import { AbilityBuilder, createMongoAbility } from "@casl/ability";
function defineEmptyAbilities(_can, _cannot) {
}
function getSalonAbilityFunction(role) {
  switch (role) {
    case "SUPER_ADMIN":
    case "ADMIN":
    case "GROUP_ADMIN":
    case "GROUP_MEMBER":
    case "FRANCHISE_ADMIN":
      return defineFranchiseAdminAbilities;
    case "OWNER":
      return defineOwnerAbilities;
    case "MANAGER":
    case "FRANCHISE_VIEWER":
      return defineManagerAbilities;
    case "WORKER":
      return defineWorkerAbilities;
    default:
      return defineEmptyAbilities;
  }
}
function getPartnerGroupAbilityFunction(role) {
  switch (role) {
    case "SUPER_ADMIN":
    case "ADMIN":
    case "GROUP_ADMIN":
      return definePartnerGroupAdminAbilities;
    case "GROUP_MEMBER":
      return definePartnerGroupMemberAbilities;
    case "FRANCHISE_VIEWER":
      return definePartnerGroupMemberAbilities;
    default:
      return defineEmptyAbilities;
  }
}
function createSalonAbilityForUser(user, salonId) {
  const { can, cannot, build } = new AbilityBuilder(createMongoAbility);
  if (!user) {
    return build();
  }
  if (user.role.includes("ADMIN") || user.role.includes("SUPER_ADMIN")) {
    defineSuperAdminAbilities(can);
    return build();
  }
  let role = null;
  for (const franchiseAccess of user.franchiseAccesses) {
    if (franchiseAccess.isActive) {
      const foundSalonAccess = franchiseAccess.salons.find(
        (salon) => salon.salonId === salonId && salon.isActive
      );
      if (foundSalonAccess) {
        role = foundSalonAccess.role;
        break;
      }
    }
  }
  for (const partnerAccess of user.partnerGroupMemberships ?? []) {
    if (partnerAccess.isActive) {
      const salons = partnerAccess.assignedFranchises?.flatMap((franchise) => franchise.salons);
      const foundSalonAccess = salons?.find((salon) => salon.salonId === salonId && salon.isActive);
      if (foundSalonAccess) {
        role = foundSalonAccess.role;
      }
    }
  }
  if (!role) {
    return build();
  }
  const abilityFunction = getSalonAbilityFunction(role);
  abilityFunction(can, cannot);
  return build();
}
function createPartnerGroupAbilityForUser(user, groupId) {
  const { can, cannot, build } = new AbilityBuilder(createMongoAbility);
  if (!user) {
    return build();
  }
  if (user.role.includes("ADMIN") || user.role.includes("SUPER_ADMIN")) {
    defineSuperAdminAbilities(can);
    return build();
  }
  const partnerAccess = user.partnerGroupMemberships?.find(
    (membership) => membership.groupId === groupId && membership.isActive
  );
  if (!partnerAccess) {
    return build();
  }
  const abilityFunction = getPartnerGroupAbilityFunction(partnerAccess.role);
  abilityFunction(can, cannot);
  return build();
}
function createSystemAbility() {
  const { can, build } = new AbilityBuilder(createMongoAbility);
  defineSuperAdminAbilities(can);
  return build();
}
function createAbilityForUser(user) {
  const { can, build } = new AbilityBuilder(createMongoAbility);
  if (!user) {
    return build();
  }
  if (user.role.includes("ADMIN") || user.role.includes("SUPER_ADMIN")) {
    defineSuperAdminAbilities(can);
  }
  return build();
}

// src/authorization/types.ts
var detectSubjectType = (item) => {
  if (typeof item === "string") {
    return item;
  }
  return item.constructor;
};
export {
  AUTHORIZATION_ACTIONS,
  AUTHORIZATION_SUBJECTS,
  ROLE_ABILITIES,
  createAbilityForUser,
  createPartnerGroupAbilityForUser,
  createSalonAbilityForUser,
  createSystemAbility,
  defineFranchiseAdminAbilities,
  defineManagerAbilities,
  defineOwnerAbilities,
  definePartnerGroupAdminAbilities,
  definePartnerGroupMemberAbilities,
  defineSuperAdminAbilities,
  defineWorkerAbilities,
  detectSubjectType,
  isAuthorizationAction,
  isAuthorizationSubject
};
//# sourceMappingURL=index.mjs.map